import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { getOidcConfig, isOidcEnabled } from './app-config';
import { resolveApiUrl } from './ultra-meet-config';

const getOidcSettings = async () => {
  const config = await getOidcConfig();

  if (!config.oidcEnabled) {
    throw new Error('OIDC is not enabled');
  }

  return {
    authority: config.oidcIssuer!,
    // Fetch the OpenID Configuration from our backend instead of the public
    // Keycloak .well-known endpoint (closed off per security requirements).
    // The backend proxies the minimal set of fields the frontend needs.
    metadataUrl: resolveApiUrl('/api/openid-configuration'),
    client_id: config.oidcClientId!,
    redirect_uri: config.oidcRedirectUri!,
    post_logout_redirect_uri: window.location.origin,
    response_type: 'code',
    scope: 'openid profile email',
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    // Request access tokens to be returned alongside ID tokens
    loadUserInfo: true,
  };
};

let userManager: UserManager | null = null;

// Function to get or create UserManager instance
const getUserManager = async (): Promise<UserManager> => {
  if (userManager) {
    return userManager;
  }

  // Check if we are in the browser environment before creating the UserManager
  if (typeof window === 'undefined') {
    throw new Error('UserManager can only be created in browser environment');
  }

  const settings = await getOidcSettings();
  userManager = new UserManager(settings);
  return userManager;
};

// Note: isOidcEnabled is now imported from app-config

// Helper function to get current user's access token
const getCurrentAccessToken = async (): Promise<string | null> => {
  try {
    if (typeof window === 'undefined') {
      return null;
    }
    
    const userManager = await getUserManager();
    const user = await userManager.getUser();
    
    return user?.access_token || null;
  } catch (error) {
    console.error('Failed to get current access token:', error);
    return null;
  }
};

// Helper function to refresh user tokens using silent signin
const refreshUserTokens = async (): Promise<string | null> => {
  const userManager = await getUserManager();

  try {
    if (typeof window === 'undefined') {
      return null;
    }  
    
    const refreshedUser = await userManager.signinSilent();
    
    return refreshedUser?.access_token || null;
  } catch (error) {
    console.error('Silent refresh failed, refresh token might be expired:', error);
    
    // If silent refresh fails, trigger full authentication flow
    try {
      await userManager.signinRedirect();
    } catch (redirectError) {
      console.error('Failed to redirect to authentication:', redirectError);
    }
    
    return null;
  }
};

export { getUserManager, getOidcSettings, isOidcEnabled, getCurrentAccessToken, refreshUserTokens };
